import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { APP_NAME } from "@crewledger/shared/constants";
import { useAuth } from "@/features/auth/auth-provider";
import { apiFetch } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InfoBanner } from "@/components/design/info-banner";

export function AdminPage() {
  const { email, logout } = useAuth();
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `Admin — ${APP_NAME}`;
  }, []);

  useEffect(() => {
    void apiFetch<{ data: { message: string } }>("/api/admin/status")
      .then((res) => setStatus(res.data.message))
      .catch(() => setStatus("Could not verify platform admin access."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-navy-deep text-white">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <CardTitle>Platform admin</CardTitle>
          <CardDescription>
            Server-verified admin access for {email ?? "your account"}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <InfoBanner variant="success">{status}</InfoBanner>
          )}
          <p className="text-sm text-muted-foreground">
            Review beta access requests, manage the allowlist, and control invitations.
          </p>
          <Link
            to="/admin/beta-access"
            className="inline-flex h-9 items-center justify-center rounded-md bg-navy-deep px-4 text-sm font-medium text-white hover:bg-navy-deep/90"
          >
            Beta access management
          </Link>
          <Link
            to="/admin/platform/features"
            className="inline-flex h-9 items-center justify-center rounded-md border px-4 text-sm font-medium"
          >
            Platform features
          </Link>
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center rounded-md border px-4 text-sm"
            onClick={() => void logout()}
          >
            Sign out
          </button>
          <p className="text-sm text-muted-foreground">
            <Link to="/" className="font-medium hover:underline">
              Back to home
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
